import { useEffect, useState } from "react";
import { Download, X, Share, PlusSquare } from "lucide-react";

const DISMISS_KEY = "invyt_pwa_dismissed";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isiOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

// Encourages visitors to install Invyt to their home screen.
// - Android/desktop Chrome: uses the native beforeinstallprompt event.
// - iOS Safari (no native prompt): shows a short "Add to Home Screen" hint.
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    let dismissed = false;
    try { dismissed = localStorage.getItem(DISMISS_KEY) === "1"; } catch { /* ignore */ }
    if (dismissed) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS never fires beforeinstallprompt — offer a manual hint after a short delay.
    let t;
    if (isiOS()) {
      t = setTimeout(() => { setIosHint(true); setVisible(true); }, 2500);
    }

    const onInstalled = () => { setVisible(false); try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ } };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (t) clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setDeferred(null);
    dismiss();
  };

  if (!visible) return null;

  return (
    <div className="fixed z-[55] left-3 right-3 bottom-3 sm:left-auto sm:right-5 sm:bottom-5 sm:w-[360px]" data-testid="pwa-install-prompt">
      <div className="rounded-2xl bg-white shadow-2xl border border-slate-100 p-4 flex items-start gap-3">
        <img src="/logo192.png?v=4" alt="Invyt" className="w-11 h-11 rounded-xl shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-heading font-bold text-slate-900 text-sm">Install Invyt</p>
          {iosHint ? (
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
              Tap <Share className="inline w-3.5 h-3.5 -mt-0.5 text-[#1E2019]" /> then{" "}
              <span className="font-medium text-slate-700">Add to Home Screen</span>
              <PlusSquare className="inline w-3.5 h-3.5 -mt-0.5 ml-0.5 text-[#1E2019]" /> to keep it one tap away.
            </p>
          ) : (
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">Add it to your home screen to keep events, tickets and your pass one tap away.</p>
          )}
          {!iosHint && (
            <button
              onClick={install}
              data-testid="pwa-install-btn"
              className="mt-2.5 h-9 px-4 rounded-full bg-[#1E2019] hover:bg-[#1E2019] text-white text-sm font-semibold inline-flex items-center gap-1.5 transition-colors"
            >
              <Download className="w-4 h-4" /> Install app
            </button>
          )}
        </div>
        <button onClick={dismiss} aria-label="Dismiss" data-testid="pwa-dismiss-btn" className="w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
